import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import { RunConfig } from '../../config/runs'

/**
 * Generates a ski run slope from a RunConfig.
 * Steeper runs = faster skiing, narrower width, more terrain noise.
 */

const SEGMENTS_PER_METER_X = 1   // grid resolution
const SEGMENTS_PER_METER_Z = 0.5
const RUNOUT_START = 0.85         // last 15% of run flattens

// Active run config (set by init)
let activeConfig: RunConfig

export function getActiveConfig(): RunConfig {
  return activeConfig
}

export function getElevation(x: number, z: number): number {
  const cfg = activeConfig
  const t = (z + cfg.length / 2) / cfg.length

  let grade = cfg.grade
  if (t > RUNOUT_START) {
    const runoutT = (t - RUNOUT_START) / (1 - RUNOUT_START)
    grade = cfg.grade * (1 - runoutT * runoutT)
  }

  let baseHeight: number
  if (t <= RUNOUT_START) {
    baseHeight = -z * cfg.grade
  } else {
    const runoutZ = -cfg.length / 2 + RUNOUT_START * cfg.length
    const heightAtRunout = -runoutZ * cfg.grade
    const dz = z - runoutZ
    const runoutT = (t - RUNOUT_START) / (1 - RUNOUT_START)
    const avgGrade = cfg.grade * (1 - runoutT * runoutT / 2)
    baseHeight = heightAtRunout - dz * avgGrade
  }

  // Terrain noise (more on harder runs)
  const noise1 = Math.sin(x * 0.05) * Math.cos(z * 0.03) * cfg.terrainNoise
  const noise2 = Math.sin(x * 0.12 + 1.5) * Math.cos(z * 0.08 + 0.7) * cfg.terrainNoise * 0.5

  // Moguls — regular bumps across the run
  let mogulHeight = 0
  if (cfg.moguls) {
    mogulHeight = Math.abs(Math.sin(x * 2.0) * Math.sin(z * 1.5)) * 0.8
  }

  // Edge banking
  const edgeFactor = Math.pow(Math.abs(x) / (cfg.width / 2), 2) * 0.5

  return baseHeight + noise1 + noise2 + mogulHeight + edgeFactor
}

export function createSlopeMesh(config: RunConfig): THREE.Mesh {
  activeConfig = config
  // Terrain extends 3x wider than the run for a full mountain feel
  const terrainWidth = config.width * 3
  const segX = Math.floor(terrainWidth * SEGMENTS_PER_METER_X * 0.5)
  const segZ = Math.floor(config.length * SEGMENTS_PER_METER_Z)

  const geometry = new THREE.PlaneGeometry(
    terrainWidth,
    config.length,
    segX,
    segZ
  )

  geometry.rotateX(-Math.PI / 2)

  const positions = geometry.attributes.position
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i)
    const z = positions.getZ(i)
    const y = getElevation(x, z)
    positions.setY(i, y)
  }

  geometry.computeVertexNormals()

  const material = new THREE.MeshStandardMaterial({
    color: 0xf0f0f0,
    roughness: 0.85,
    metalness: 0.0,
    flatShading: false,
  })

  const mesh = new THREE.Mesh(geometry, material)
  mesh.receiveShadow = true
  return mesh
}

export function createSlopePhysicsBody(config: RunConfig, world: CANNON.World): CANNON.Body {
  const slopeAngle = Math.atan(config.grade)

  const body = new CANNON.Body({
    mass: 0,
    material: new CANNON.Material('snow'),
  })

  const planeShape = new CANNON.Plane()
  body.addShape(planeShape)

  body.quaternion.setFromEuler(-Math.PI / 2 + slopeAngle, 0, 0)
  body.position.set(0, config.length / 2 * config.grade, -config.length / 2)

  world.addBody(body)
  return body
}
